import React, { Component, useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination, Navigation } from 'swiper/core';
import { withRouter, Redirect } from 'react-router-dom';
import { onSnapshot } from 'firebase/firestore';

import Candle from '../Candle/index.js';
import Spinner from '../Spinner/index.js';
import AgeLevelNavigation from '../AgeLevelNavigation/index.js';
import { withFirebase } from '../Firebase/index.js';

import * as ROUTES from '../../constants/routes.js';
import useLocalStorage from '../../useLocalStorage';
import { getToday } from '../../utils.js';

import 'swiper/swiper.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/navigation/navigation.min.css';

// icons
import bibleTime from '../../images/bible-time.svg';
import bigQuestion from '../../images/big-question.svg';
import goAndTell from '../../images/go-and-tell.svg';
import snapshot from '../../images/snapshot.svg';
import song from '../../images/song.svg';
import windingDown from '../../images/winding-down.svg';

SwiperCore.use([Pagination, Navigation]);

const DayBase = ({ day, errorMessage }) => {
	const [ageLevel, setAgeLevel] = useLocalStorage('ageLevel', '');
	const [videoActive, setVideoActive] = useState(false);
	const [lastSlide, setLastSlide] = useState(false);

	useEffect(() => {
		const activeSlide = document.getElementsByClassName('swiper-slide-active')[0];
		checkActiveSlide(activeSlide);
	}, [ageLevel]);

	if (!ageLevel) {
		setAgeLevel('younger');
	}

	const checkActiveSlide = (activeSlide) => {
		if (activeSlide.classList.contains('video')) {
			setVideoActive(true);
		} else {
			setVideoActive(false);
		}

		if (activeSlide.classList.contains('last-slide')) {
			setLastSlide(true);
		} else {
			setLastSlide(false);
		}
	}

	const slideChangeHandler = (swiper) => {
		const activeSlide = swiper.slides[swiper.activeIndex];
		checkActiveSlide(activeSlide);
	};

	const [swiperInstance, setSwiperInstance] = useState(null);

	const handleParentsClick = (event) => {
		const swiperContainer = document.getElementsByClassName('swiper-container')[0];
		const parentsContainer = document.getElementsByClassName('parents-container')[0];
		const nav = document.getElementsByClassName('age-level')[0];
		const swipeNote = document.getElementsByClassName('swipe-note')[0];
		const parentsNav = document.getElementsByClassName('parents-nav')[0];


		swiperContainer.classList.toggle('hidden');
		parentsContainer.classList.toggle('hidden');
		swipeNote.classList.toggle('hidden');

		nav.classList.toggle('parents-active');
		parentsNav.classList.toggle('parents-active');
	}

	const getHeadingIcon = (heading) => {
		let icon;
		
		switch(heading) {
			case 'Bible Time':
				icon = bibleTime;
				break;
			case 'Big Question':
				icon = bigQuestion;
				break;
			case 'Go and Tell':
				icon = goAndTell;
				break;
			case 'Snapshot':
				icon = snapshot;
				break;
			case 'Song':
				icon = song;
				break;
			case 'Winding Down':
				icon = windingDown;
				break;
			default:
				icon = null;
		}

		if (icon) {
			return <img className="icon" src={icon} alt={heading} />
		}
	}

	let headingIcon = day[ageLevel]?.heading && getHeadingIcon(day[ageLevel]?.heading);

	return (
		<>
			<section id="day-container" className={ videoActive ? 'video-active' : null }>
				<div className="wrapper">
					<AgeLevelNavigation ageLevel={ageLevel} setAgeLevel={setAgeLevel} clickHandler={handleParentsClick} />
					<Swiper
						spaceBetween={105}
						pagination={{ clickable: true }}
						navigation={true}
						onSlideChange={slideChangeHandler}
						observer={true}
						observeParents={true}
						onSwiper={(swiper) => setSwiperInstance(swiper)}
					>
						<SwiperSlide>
							<h2 className="display-date">{day.displayDate}</h2>

							<h3>{ day.opening?.heading ? day.opening?.heading : "Welcome" }</h3>
						
							{ day.opening?.text && day.opening?.text !== '<p><br></p>' ?
								<div dangerouslySetInnerHTML={{ __html: day.opening?.text }} />
								:
								<p>Welcome to CabinTime! Swipe to the right to start a session.</p>
							}
						</SwiperSlide>

						{/* Default highs and lows slides if no data for the day */}
						{ Object.keys(day).length === 0 &&
							<>
							<SwiperSlide key="highs">
								<h3>Highs and lows</h3>
								<p>Look back on your day and identify the moment that was the best for you&mdash;your high moment of the day. What made it best?</p>
								<p>Where was God during the best moment of your day?</p>
							</SwiperSlide>

							<SwiperSlide key="lows">
								<h3>Highs and lows</h3>
								<p>Look back on your day and identify the moment that was most challenging, difficult, or sad for you&mdash;your low moment of the day. What made it your low?</p>
								<p>Where was God during the low moment of your day?</p>
							</SwiperSlide>
							</>
						}

						{ day[ageLevel] &&
							<>
							{ day[ageLevel]?.text && day[ageLevel]?.text !== '<p><br></p>' &&
								<SwiperSlide key="text">
									<div>
										{ day[ageLevel]?.heading && headingIcon }
										<h2 className="heading">
											{ day[ageLevel]?.heading ? day[ageLevel].heading : <>Today&rsquo;s session</> }
										</h2>
									</div>
									<div dangerouslySetInnerHTML={{ __html:day[ageLevel]?.text }} />
								</SwiperSlide>
							}

							{ day[ageLevel]?.video &&
								<SwiperSlide className="video" key="video">
									<div className="video-heading">
										{ day[ageLevel]?.heading && headingIcon }
										<h2 className="heading">
											{ day[ageLevel]?.heading ? day[ageLevel].heading : <>Today&rsquo;s session</> }
										</h2>
									</div>

									<div className="video-container">
										<ReactPlayer
											className="react-player"
											width="100%"
											height="auto"
											url={day[ageLevel]?.video}
											controls={true}
										/>
									</div>
								</SwiperSlide>
							}
							</>
						}

						{ day.prayer && day.prayer !== '<p><br></p>' &&
							<SwiperSlide>
								<h3>Prayer</h3>
								<div dangerouslySetInnerHTML={{ __html: day.prayer }} />
							</SwiperSlide>
						}

						<SwiperSlide className='last-slide'>
							<h2>See you tomorrow in our <span className="no-wrap">next session!</span></h2>
						</SwiperSlide>
					</Swiper>

					{ day.parents && day.parents !== '<p><br></p>' &&
						<div className='parents-container hidden'>
							<div className='parents'>
								<h2 className='heading'>For parents</h2>
								<div dangerouslySetInnerHTML={{ __html: day.parents }} />
							</div>
						</div>
					}
				</div>

				<Candle />
			</section>

			<footer>
				<div>
					<div>
						{ day.parents && day.parents !== '<p><br></p>' &&
							<p className='parents-nav' onClick={handleParentsClick}>
								<span className='for-parents'>For parents</span>
								<span className='return'>Return to slides</span>
							</p>
						}
					</div>

					<div>
						<p className={`swipe-note ${ lastSlide && 'last-slide-active' } `} onClick={ () => swiperInstance?.slideNext() }>Swipe to continue</p>
					</div>

					<div>
						<p className='help'><a href='/help'>Help</a></p>
					</div>
				</div>
			</footer>
		</>
	);
};

const errorMessage = 'Content not found. Check back later.';

class Day extends Component {
	constructor(props) {
		super(props);

		// query string for testing
		const queryString = this.props.location.search;
		const params = new URLSearchParams(queryString);
		const today = params.get('day') ?? getToday();

		this.state = {
			loading: false,
			day: {},
			dayId: today
		};
	}
		
	componentDidMount() {
		this.setState({ 
			loading: true
		});

		this.fetchData(this.state.dayId);
	}

	componentWillUnmount() {
		this.unsubscribe();
	}

	fetchData(id) {
		this.unsubscribe = onSnapshot(this.props.firebase.day(id), (doc) => {
			if (!doc.data()) {
				this.setState({
					loading: false,
					errorMessage: errorMessage
				});
			} else {
				const data = doc.data();
				
				this.setState({
					loading: false,
					day: data
				});
			}
		}, (error) => {
			console.error(error);
			this.props.history.push(ROUTES.ERROR, { error: error });
		});
	}

	render() {
		const {
			loading,
			dayId,
			day,
			errorMessage
		} = this.state;

		if (loading) {
			return (
				<section>
					<div className="wrapper">
						<Spinner />
					</div>
				</section>
			);
		}

		if (dayId) {
			return (
				<DayBase key={dayId} id={dayId} day={day} errorMessage={errorMessage} />
			);
		} else {
			return <Redirect to={ROUTES.NOT_FOUND} />
		}
	}
}

export default withRouter(withFirebase(Day));